import React, { Suspense, useEffect } from "react";
import "./App.css";
import "@/integrations/supabase/storage";
import { initLogCollector } from "@/utils/logCollector";
import { runAppWarmup } from "@/utils/appWarmup";
import { lazyWithRetry } from "@/utils/chunkErrorHandler";

// Initialize log collector early to capture all console output
initLogCollector();
import { Toaster as Sonner } from "sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/auth";
import { ClaimsProvider } from "@/contexts/ClaimsContext";
import { DebugTimeLimitProvider } from "@/contexts/DebugTimeLimitContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { HelmetProvider } from "react-helmet-async";

// Eagerly loaded pages (part of auth flow, needed immediately)
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages — each gets its own chunk, loaded on first navigation.
// `lazyWithRetry` recovers from stale-deploy chunk errors by reloading once.
const Index = lazyWithRetry(() => import("./pages/Index"));
const TutorPage = lazyWithRetry(() => import("./pages/TutorPage"));
const ChallengesPage = lazyWithRetry(() => import("./pages/ChallengesPage"));
const IdeasPage = lazyWithRetry(() => import("./pages/IdeasPage"));
const AchievementsPage = lazyWithRetry(() => import("./pages/AchievementsPage"));
const StatsPage = lazyWithRetry(() => import("./pages/StatsPage"));
const StudentClassPage = lazyWithRetry(() => import("./pages/StudentClassPage"));
const InvitePage = lazyWithRetry(() => import("./pages/InvitePage"));
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"));
const ClaimPage = lazyWithRetry(() => import("./pages/ClaimPage"));
const ChangePasswordPage = lazyWithRetry(() => import("./pages/ChangePasswordPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const ContactPage = lazyWithRetry(() => import("./pages/ContactPage"));
const AboutPage = lazyWithRetry(() => import("./pages/AboutPage"));
const HallOfFamePage = lazyWithRetry(() => import("./pages/HallOfFamePage"));

// Teacher pages
const TeacherClassesPage = lazyWithRetry(() => import("./pages/teacher/TeacherClassesPage"));
const ClassDetailPage = lazyWithRetry(() => import("./pages/teacher/ClassDetailPage"));
const TeacherCreateClassPage = lazyWithRetry(
  () => import("./pages/teacher/TeacherCreateClassPage")
);
const TeacherChallengesPage = lazyWithRetry(() => import("./pages/teacher/TeacherChallengesPage"));
const TeacherChallengeDetailPage = lazyWithRetry(
  () => import("./pages/teacher/TeacherChallengeDetailPage")
);
const TeacherGroupsPage = lazyWithRetry(() => import("./pages/teacher/TeacherGroupsPage"));
const TeacherInspirationPage = lazyWithRetry(
  () => import("./pages/teacher/TeacherInspirationPage")
);

// Admin pages
const AdminTestingPage = lazyWithRetry(() => import("./pages/admin/AdminTestingPage"));
const AdminUsersPage = lazyWithRetry(() => import("./pages/admin/AdminUsersPage"));
const AdminInviteManagementPage = lazyWithRetry(
  () => import("./pages/admin/AdminInviteManagementPage")
);
const AdminClassesPage = lazyWithRetry(() => import("./pages/admin/AdminClassesPage"));
const AdminClassDetailPage = lazyWithRetry(() => import("./pages/admin/AdminClassDetailPage"));
const AdminSafetyPage = lazyWithRetry(() => import("./pages/admin/AdminSafetyPage"));
const AdminStatsPage = lazyWithRetry(() => import("./pages/admin/AdminStatsPage"));
const AdminMessagesPage = lazyWithRetry(() => import("./pages/admin/AdminMessagesPage"));
const AdminBulkCreateClassPage = lazyWithRetry(
  () => import("./pages/admin/AdminBulkCreateClassPage")
);

import { useActivityTracking } from "./hooks/useActivityTracking";
import { useBodyScrollFix } from "./hooks/useBodyScrollFix";
import { useScrollToTop } from "./hooks/useScrollToTop";
import { useNewAssignmentNotifier } from "./hooks/challenges/useNewAssignmentNotifier";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import OfflineBanner from "./components/layout/OfflineBanner";
import { DefaultPasswordGuard } from "./components/auth/DefaultPasswordGuard";
import { SessionExpiredDialog } from "./components/auth/SessionExpiredDialog";
import { RouteErrorBoundary } from "./components/SectionErrorBoundary";
import { useLocation } from "react-router-dom";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

import { AchievementsManager } from "./components/tutor/AchievementsManager";
import BugReportButton from "./components/feedback/BugReportButton";

const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-pulse text-muted-foreground text-sm">Načítání...</div>
  </div>
);

// Application root with activity tracking
const AppWithActivityTracking = () => {
  const { profile } = useAuth();
  useActivityTracking("app_usage");
  useBodyScrollFix(); // Fix stuck body scroll from modals
  useNewAssignmentNotifier();

  // Run app warmup tasks on startup (fire and forget, skip for students)
  useEffect(() => {
    if (profile && profile.role !== "student") {
      runAppWarmup();
    }
  }, [profile]);

  return (
    <BrowserRouter>
      <OfflineBanner />
      <AchievementsManager />
      <BugReportButton />
      <SessionExpiredDialog />
      <AppWithPersistentNavbar />
    </BrowserRouter>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  useScrollToTop();
  return null;
};

// Persistent Navbar Wrapper - only renders on authenticated, non-auth pages
const PersistentNavbarWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Don't show navbar on auth page or when not authenticated
  if (!user || location.pathname === "/auth") {
    return null;
  }

  return <Navbar />;
};

// Persistent Footer Wrapper - only renders on authenticated, non-auth pages
const PersistentFooterWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Don't show footer on auth page or when not authenticated
  if (!user || location.pathname === "/auth") {
    return null;
  }

  return <Footer />;
};

// App component with persistent navbar that doesn't re-render on navigation
const AppWithPersistentNavbar = () => {
  const { loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Scroll to top on navigation */}
      <ScrollToTop />

      {/* Persistent Navbar - only show for authenticated users on non-auth pages */}
      <PersistentNavbarWrapper />

      {/* Main content area that changes on navigation */}
      <main className="flex-1">
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/invite/:inviteUuid" element={<InvitePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DefaultPasswordGuard>
                    <RouteErrorBoundary section="Domů">
                      <Index />
                    </RouteErrorBoundary>
                  </DefaultPasswordGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary section="Profil">
                    <ProfilePage />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/claim"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary section="Claim">
                    <ClaimPage />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary section="Kontakt">
                    <ContactPage />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/about"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary section="O aplikaci">
                    <AboutPage />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/hall-of-fame"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary section="Síň slávy">
                    <HallOfFamePage />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* AI Tutor - pouze pro žáky */}
            <Route
              path="/tutor"
              element={
                <StudentRouteGuard>
                  <DefaultPasswordGuard>
                    <RouteErrorBoundary section="AI Tutor">
                      <TutorPage />
                    </RouteErrorBoundary>
                  </DefaultPasswordGuard>
                </StudentRouteGuard>
              }
            />

            {/* Cesty pouze pro žáky */}
            <Route
              path="/challenges"
              element={
                <StudentRouteGuard>
                  <DefaultPasswordGuard>
                    <RouteErrorBoundary section="Výzvy">
                      <ChallengesPage />
                    </RouteErrorBoundary>
                  </DefaultPasswordGuard>
                </StudentRouteGuard>
              }
            />
            <Route
              path="/ideas"
              element={
                <StudentRouteGuard>
                  <DefaultPasswordGuard>
                    <RouteErrorBoundary section="Nápady">
                      <IdeasPage />
                    </RouteErrorBoundary>
                  </DefaultPasswordGuard>
                </StudentRouteGuard>
              }
            />
            <Route
              path="/my-class"
              element={
                <StudentRouteGuard>
                  <DefaultPasswordGuard>
                    <RouteErrorBoundary section="Moje třída">
                      <StudentClassPage />
                    </RouteErrorBoundary>
                  </DefaultPasswordGuard>
                </StudentRouteGuard>
              }
            />
            <Route
              path="/achievements"
              element={
                <StudentRouteGuard>
                  <DefaultPasswordGuard>
                    <RouteErrorBoundary section="Úspěchy">
                      <AchievementsPage />
                    </RouteErrorBoundary>
                  </DefaultPasswordGuard>
                </StudentRouteGuard>
              }
            />
            <Route
              path="/stats"
              element={
                <StudentRouteGuard>
                  <DefaultPasswordGuard>
                    <RouteErrorBoundary section="Statistiky">
                      <StatsPage />
                    </RouteErrorBoundary>
                  </DefaultPasswordGuard>
                </StudentRouteGuard>
              }
            />

            {/* Cesty pouze pro učitele */}
            <Route
              path="/teacher/classes"
              element={
                <TeacherRouteGuard>
                  <RouteErrorBoundary section="Třídy">
                    <TeacherClassesPage />
                  </RouteErrorBoundary>
                </TeacherRouteGuard>
              }
            />
            <Route
              path="/teacher/classes/new"
              element={
                <TeacherRouteGuard>
                  <RouteErrorBoundary section="Nová třída">
                    <TeacherCreateClassPage />
                  </RouteErrorBoundary>
                </TeacherRouteGuard>
              }
            />
            <Route
              path="/teacher/classes/:classId"
              element={
                <TeacherRouteGuard>
                  <RouteErrorBoundary section="Detail třídy">
                    <ClassDetailPage />
                  </RouteErrorBoundary>
                </TeacherRouteGuard>
              }
            />
            <Route
              path="/teacher/challenges"
              element={
                <TeacherRouteGuard>
                  <RouteErrorBoundary section="Výzvy učitele">
                    <TeacherChallengesPage />
                  </RouteErrorBoundary>
                </TeacherRouteGuard>
              }
            />
            <Route
              path="/teacher/challenges/:challengeId"
              element={
                <TeacherRouteGuard>
                  <RouteErrorBoundary section="Detail výzvy">
                    <TeacherChallengeDetailPage />
                  </RouteErrorBoundary>
                </TeacherRouteGuard>
              }
            />
            <Route
              path="/teacher/groups"
              element={
                <TeacherRouteGuard>
                  <RouteErrorBoundary section="Skupiny">
                    <TeacherGroupsPage />
                  </RouteErrorBoundary>
                </TeacherRouteGuard>
              }
            />
            <Route
              path="/teacher/inspiration"
              element={
                <TeacherRouteGuard>
                  <RouteErrorBoundary section="Inspirace">
                    <TeacherInspirationPage />
                  </RouteErrorBoundary>
                </TeacherRouteGuard>
              }
            />

            {/* Cesty pouze pro administrátory */}
            <Route
              path="/admin/users"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Správa uživatelů">
                    <AdminUsersPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />
            <Route
              path="/admin/testing"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Testování">
                    <AdminTestingPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />
            <Route
              path="/admin/invites"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Správa pozvánek">
                    <AdminInviteManagementPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />
            <Route
              path="/admin/classes"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Správa tříd">
                    <AdminClassesPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />
            <Route
              path="/admin/classes/:classId"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Detail třídy">
                    <AdminClassDetailPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />
            <Route
              path="/admin/safety"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Bezpečnost">
                    <AdminSafetyPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />
            <Route
              path="/admin/stats"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Statistiky">
                    <AdminStatsPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />
            <Route
              path="/admin/messages"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Správa zpráv">
                    <AdminMessagesPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />
            <Route
              path="/admin/bulk-create-class"
              element={
                <AdminRouteGuard>
                  <RouteErrorBoundary section="Hromadné vytvoření třídy">
                    <AdminBulkCreateClassPage />
                  </RouteErrorBoundary>
                </AdminRouteGuard>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {/* Persistent Footer */}
      <PersistentFooterWrapper />
    </div>
  );
};

// Základní ochrana pro přihlášené uživatele
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  return <>{children}</>;
};

// Komponenta pro zabezpečené cesty podle role
const StudentRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  if (!profile) {
    return <div>Loading...</div>;
  }

  const userRole = profile.role;

  if (userRole !== "student") {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

// Komponenta pro zabezpečené cesty pro učitele
const TeacherRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  // Wait for profile to load before making role-based decisions
  if (!profile) {
    return <div>Loading...</div>;
  }

  const userRole = profile.role;

  if (userRole !== "teacher") {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

// Komponenta pro zabezpečené cesty pro správce
const AdminRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  // Wait for profile to load before making role-based decisions
  if (!profile) {
    return <div>Loading...</div>;
  }

  const userRole = profile.role;

  if (userRole !== "admin") {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <ClaimsProvider>
            <DebugTimeLimitProvider>
              <ThemeProvider>
                <HelmetProvider>
                  <Sonner
                    position="bottom-right"
                    duration={3000}
                    richColors
                    closeButton
                    expand={true}
                    visibleToasts={3}
                    toastOptions={{
                      className: "text-sm py-2 px-3",
                    }}
                  />
                  <AppWithActivityTracking />
                </HelmetProvider>
              </ThemeProvider>
            </DebugTimeLimitProvider>
          </ClaimsProvider>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
